import { formatPhoneDisplay } from '@mdvip/shared/lib/phone';
import { isValid, parseISO } from 'date-fns';

/** How a dataset field's value is displayed. One list serves the table and, later, the CSV. */
export type FieldFormat = 'date' | 'currency' | 'number' | 'yesBlank' | 'yesNoBlank' | 'phone' | 'text';

/**
 * The locale-aware pieces of formatting, injected so this module stays pure. The caller builds
 * them from the shared `useFormatDate` / `useCurrency` hooks and the active locale.
 */
export interface MemberViewFormatters {
  date: (dateOnly: string) => string;
  currency: (amount: number) => string;
  number: (n: number) => string;
  yes: string;
  no: string;
}

/**
 * Parses an API date (`yyyy-MM-dd`, no time) as that local calendar date, or null when it's
 * missing or unparseable.
 *
 * `parseISO`, not `new Date(...)`: the latter reads a bare date as UTC midnight, which is the
 * previous day anywhere west of Greenwich.
 */
export function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

/** A field value as display text. Missing values render blank in every format. */
export function formatFieldValue(
  value: unknown,
  format: FieldFormat | undefined,
  formatters: MemberViewFormatters,
): string {
  if (value === null || value === undefined) return '';

  switch (format) {
    case 'date':
      return typeof value === 'string' && parseDateOnly(value) ? formatters.date(value) : '';
    case 'currency':
      return typeof value === 'number' ? formatters.currency(value) : '';
    case 'number':
      return typeof value === 'number' ? formatters.number(value) : '';
    case 'yesBlank':
      return value === true ? formatters.yes : '';
    case 'yesNoBlank':
      return value === true ? formatters.yes : value === false ? formatters.no : '';
    case 'phone':
      return typeof value === 'string' ? formatPhoneDisplay(value) : '';
    default:
      return String(value);
  }
}
